import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Printer, Phone, FileText, AlertTriangle, Clock, ShieldAlert,
  ClipboardCheck, Copy, Check, UserCheck, Ambulance, FileCheck2, Users,
} from 'lucide-react';
import Header from '../components/Header';
import { SECTIONS } from '../lib/sectionTheme';

const P = SECTIONS.sops;

const RIS_PHRASES = [
  {
    id: 'standard',
    label: 'Standard in-hours communication',
    text: 'Urgent findings communicated to Dr [NAME] at [TIME] via practice staff. Radiologist consultation offered; GP [accepted/declined]. Management plan: [PLAN].',
  },
  {
    id: 'after-hours',
    label: 'After-hours / unavailable GP — ED directed',
    text: 'Urgent findings identified. Referring practitioner unavailable due to [after-hours / public holiday / long weekend]. Patient advised to attend Emergency Department immediately. Clinical reasoning: [REASON]. Patient contacted at [TIME] by [STAFF NAME].',
  },
  {
    id: 'rad-busy',
    label: 'Radiologist unavailable at first call',
    text: 'Call received by Dr [GP NAME] at [TIME]. Radiologist in procedure; callback prioritised. GP [accepted key findings via staff / requested callback].',
  },
  {
    id: 'patient-direct',
    label: 'Patient direct communication',
    text: 'Patient contacted directly at [TIME] by [STAFF NAME] due to GP unavailability and urgent clinical risk. Patient informed of finding, advised of required action: [ACTION]. Patient acknowledged and agreed to proceed. GP notified via [METHOD] for continuity of care.',
  },
];

const CRITICAL = [
  'Deep vein thrombosis (DVT)',
  'Abscess requiring urgent drainage',
  'Acute infection',
  'Vascular emergency',
  'Spinal cord compression',
  'Significant procedural complication',
];

const URGENT = [
  'Suspicious malignancy',
  'Clinically significant incidental findings requiring prompt review',
];

export default function UrgentFindingsPage() {
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState(null);

  const copy = async (id, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // noop — rare
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, .min-h-screen { background: white !important; }
          .print-container { padding: 0 !important; max-width: none !important; }
          h1, h2, h3 { color: #0E2D5C !important; }
        }
      `}</style>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 print-container" data-testid="urgent-findings-page">
        {/* Top bar */}
        <div className="no-print flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/sops')}
            data-testid="uf-back"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to SOPs
          </button>
          <button
            onClick={() => window.print()}
            data-testid="uf-print"
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-lg text-white"
            style={{ backgroundColor: P.color }}
          >
            <Printer className="w-3.5 h-3.5" /> Print quick-ref
          </button>
        </div>

        {/* Cover */}
        <div className="rounded-2xl border p-6 sm:p-8 mb-6" style={{ borderColor: `${P.color}33`, background: `linear-gradient(135deg, ${P.color}0A, transparent 60%)` }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#DC262615', color: '#DC2626' }}>
              <AlertTriangle className="w-6 h-6" />
            </div>
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full" style={{ backgroundColor: `${P.color}1A`, color: P.color }}>
              SOP · v1.0 · DIAS Std 6
            </span>
          </div>
          <h1 className="font-['Outfit'] text-2xl sm:text-3xl font-bold text-foreground tracking-tight">Urgent &amp; Critical Findings — Quick Reference</h1>
          <p className="text-sm text-muted-foreground mt-1">Closed-loop communication model · After-hours policy · RIS copy-paste phrases</p>
          <div className="mt-5 flex flex-wrap gap-5 text-xs">
            <div><span className="uppercase tracking-wider text-[10px] font-bold text-muted-foreground">Owner</span><div className="text-foreground font-semibold mt-0.5">Medical Director</div></div>
            <div><span className="uppercase tracking-wider text-[10px] font-bold text-muted-foreground">Governance</span><div className="text-foreground font-semibold mt-0.5">Practice Manager</div></div>
            <div><span className="uppercase tracking-wider text-[10px] font-bold text-muted-foreground">Applies to</span><div className="text-foreground font-semibold mt-0.5">Radiologists · Radiographers · Senior Reception</div></div>
          </div>
        </div>

        {/* Golden rule */}
        <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-5 mb-6" data-testid="uf-core-principle">
          <div className="flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 mt-0.5 shrink-0 text-red-600" />
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-red-600">Core Principle</p>
              <p className="text-base font-semibold text-foreground mt-1">
                No urgent or critical finding leaves the practice without confirmed communication and a documented management pathway.
              </p>
            </div>
          </div>
        </div>

        {/* Definitions — two columns */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <DefCard icon={AlertTriangle} tone="#DC2626" label="Critical" desc="Immediate action" items={CRITICAL} />
          <DefCard icon={Clock} tone="#D97706" label="Urgent" desc="Same-day action" items={URGENT} />
        </div>

        {/* Flow — in-hours */}
        <h2 className="font-['Outfit'] text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold" style={{ backgroundColor: `${P.color}1A`, color: P.color }}>1</span>
          In-Hours Flow — Tiered Communication
        </h2>
        <div className="space-y-2 mb-8" data-testid="uf-flow-inhours">
          <FlowStep n="1" icon={UserCheck} tone={P.color} title="Radiologist identifies & classifies" body="Reviews study. Classifies as URGENT or CRITICAL. Flags case in RIS." />
          <FlowStep n="2" icon={Phone} tone={P.color} title="Senior reception initiates contact (delegated)" body={<>Uses standard script: <em>"This is SP Radiology calling regarding an urgent result for your patient. Would you like to speak directly with the radiologist, or are you comfortable receiving the key findings now?"</em></>} />
          <FlowBranch
            title="3 — GP response pathway"
            a={{ label: 'Option A — GP requests radiologist', body: 'Call transferred immediately. If radiologist is in a procedure, callback prioritised and GP informed.' }}
            b={{ label: 'Option B — GP accepts via staff', body: 'Reception communicates key findings + recommended action. GP provides management plan (ED · commence treatment · urgent review).' }}
            color={P.color}
          />
          <FlowStep n="4" icon={ClipboardCheck} tone={P.color} title="Document in RIS (mandatory)" body="Time · Person spoken to · Findings summary · Whether radiologist contact was requested · Management instructions." />
          <FlowStep n="5" icon={FileCheck2} tone={P.color} title="Report documentation" body="Final report must include the standardised wording — see RIS Phrase Templates below." />
        </div>

        {/* Flow — after-hours */}
        <h2 className="font-['Outfit'] text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold" style={{ backgroundColor: '#DC262615', color: '#DC2626' }}>2</span>
          After-Hours &amp; Extended Non-Working Period
        </h2>
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/5 p-4 mb-4 text-sm text-foreground">
          <strong>Covers:</strong> evenings &amp; nights · weekends · public holidays · extended periods (long weekends ≥ 2 days).<br />
          <strong>Rule:</strong> urgent findings MUST NOT be delayed because the GP is unavailable.
        </div>
        <div className="space-y-2 mb-8" data-testid="uf-flow-afterhours">
          <FlowStep n="1" icon={Phone} tone="#D97706" title="Attempt GP contact" body="Call the clinic. Leave an urgent message. Log the attempt." />
          <FlowStep n="2" icon={UserCheck} tone="#D97706" title="Radiologist assesses clinical risk" body="Can this safely wait until the GP is next available?" />
          <FlowStep n="3" icon={Ambulance} tone="#DC2626" title="If NOT safe — direct patient to ED IMMEDIATELY" highlight body="Same-day risk · overnight risk · delay over a long weekend." />
          <FlowStep n="4" icon={Users} tone="#D97706" title="Inform the patient" body="Finding is urgent · delay is unsafe · immediate ED attendance required." />
          <FlowStep n="5" icon={ClipboardCheck} tone="#D97706" title="Document everything" body="Contact attempts · GP unavailability · clinical reasoning · patient advice." />
          <FlowStep n="6" icon={FileCheck2} tone="#D97706" title="Report wording" body={<><em>"Urgent findings identified. Referring practitioner unavailable due to after-hours / extended public holiday period. Patient advised to attend Emergency Department immediately."</em></>} />
        </div>

        {/* RIS phrase templates */}
        <h2 className="font-['Outfit'] text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-bold" style={{ backgroundColor: `${P.color}1A`, color: P.color }}>3</span>
          RIS Phrase Templates <span className="text-xs font-normal text-muted-foreground">(tap to copy)</span>
        </h2>
        <div className="space-y-3 mb-8" data-testid="uf-ris-phrases">
          {RIS_PHRASES.map(p => (
            <div key={p.id} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-center justify-between gap-3 mb-2">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{p.label}</p>
                <button
                  onClick={() => copy(p.id, p.text)}
                  data-testid={`uf-copy-${p.id}`}
                  className="no-print inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-md border transition-colors"
                  style={copiedId === p.id
                    ? { borderColor: '#16A34A', color: '#16A34A', backgroundColor: '#16A34A10' }
                    : { borderColor: `${P.color}40`, color: P.color }}
                >
                  {copiedId === p.id ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>
              <p className="text-sm text-foreground leading-relaxed font-mono bg-muted/60 rounded-md p-3">{p.text}</p>
            </div>
          ))}
        </div>

        {/* Responsibility + audit footer */}
        <div className="grid sm:grid-cols-2 gap-4 mb-8">
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Responsibility Matrix</p>
            <table className="w-full text-sm">
              <tbody>
                {[
                  ['Radiologist', 'Clinical interpretation & escalation decision'],
                  ['Senior Reception', 'Initial GP communication'],
                  ['Radiographer', 'Escalation support'],
                  ['Practice Manager', 'Audit and compliance'],
                ].map(([r, d]) => (
                  <tr key={r} className="border-b border-border/60 last:border-0">
                    <td className="py-1.5 pr-3 font-semibold text-foreground whitespace-nowrap">{r}</td>
                    <td className="py-1.5 text-foreground/80">{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="rounded-xl border border-border bg-card p-5">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-3">Monthly Audit Checklist</p>
            <ul className="space-y-2 text-sm text-foreground">
              {['% of urgent findings communicated', 'Time to communication', 'Documentation completeness', 'Compliance with escalation protocol']
                .map((c, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="w-4 h-4 rounded border border-border mt-0.5 shrink-0" />
                    <span>{c}</span>
                  </li>
                ))}
            </ul>
          </div>
        </div>

        <p className="no-print text-center text-[11px] text-muted-foreground mb-2">
          Owner: Medical Director · Governance: Practice Manager · Review: Annual
        </p>
      </div>
    </div>
  );
}

function DefCard({ icon: Icon, tone, label, desc, items }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${tone}15`, color: tone }}>
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <p className="font-['Outfit'] text-sm font-bold" style={{ color: tone }}>{label}</p>
          <p className="text-[11px] text-muted-foreground -mt-0.5">{desc}</p>
        </div>
      </div>
      <ul className="text-sm text-foreground space-y-1">
        {items.map((t, i) => (
          <li key={i} className="flex gap-2"><span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: tone }} /><span>{t}</span></li>
        ))}
      </ul>
    </div>
  );
}

function FlowStep({ n, icon: Icon, tone, title, body, highlight = false }) {
  return (
    <div
      className="flex gap-3 rounded-xl border p-4"
      style={highlight
        ? { borderColor: `${tone}`, backgroundColor: `${tone}0A` }
        : { borderColor: 'hsl(var(--border))' }}
    >
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 text-xs font-bold" style={{ backgroundColor: `${tone}15`, color: tone }}>
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-foreground flex items-center gap-1.5">
          <Icon className="w-3.5 h-3.5" style={{ color: tone }} /> {title}
        </p>
        <div className="text-xs text-foreground/80 mt-1 leading-relaxed">{body}</div>
      </div>
    </div>
  );
}

function FlowBranch({ title, a, b, color }) {
  return (
    <div className="rounded-xl border border-border p-4">
      <p className="text-sm font-semibold text-foreground mb-3">{title}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {[a, b].map((opt, i) => (
          <div key={i} className="rounded-lg p-3 text-xs border" style={{ borderColor: `${color}33`, backgroundColor: `${color}0A` }}>
            <p className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color }}>{opt.label}</p>
            <p className="text-foreground/85 leading-relaxed">{opt.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
